import { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Grid,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Container,
} from '@mantine/core';
import {
  IconCircleDashedPlus,
  IconSparkles,
  IconTrash,
  IconPlus,
  IconArrowLeft,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '../../components';
import { useGetTranchesDataMutation, useSaveCustomizedMixesMutation } from '../../services/api';
import { setMixes } from '../../data/features/mixes/mixes';
import CustomizedMixTrancheDetails from '@/components/CustomizedMixTrancheDetails';
import CustomizedMixTotalBox from '@/components/CustomizedMixTotalBox';
import classes from './style.module.css';
import CustomizedMixTotalAmountIcon from '../../assets/images/customizedMixTotalAmountIcon.svg?react'; // eslint-disable-line import/no-unresolved
import CustomizedMixTotalReturnIcon from '../../assets/images/customizedMixTotalReturnIcon.svg?react'; // eslint-disable-line import/no-unresolved
import CustomizedMixTotalInterestIcon from '../../assets/images/customizedMixTotalInterestIcon.svg?react'; // eslint-disable-line import/no-unresolved
import CustomizedMixMaxReturnIcon from '../../assets/images/customizedMixMaxReturnIcon.svg?react'; // eslint-disable-line import/no-unresolved
import routes from '@/routes';
import { formatNumberWithCommas } from '@/helpers/string';
import { calculateTotalsForMix } from './functions';
import { loanGoalOptions } from '@/helpers/enums';
import { getMutationResult } from '@/helpers/query';

/**
 * Validate tranche fields
 */
const validateTrancheField = (value, errorMessage) => {
  if (!value || value === 0 || value === '') {
    return errorMessage;
  }
  return null;
};

const CustomizedMix = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const MAX_MIXES = 5;

  const storeMixes = useSelector((state) => state.mixes.mixes);

  // State for the active tab
  const [activeTab, setActiveTab] = useState('mix-0');

  const [tranchesData, setTranchesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [getTranchesData] = useGetTranchesDataMutation();
  const [apiSaveCustomizedMixes] = useSaveCustomizedMixesMutation();

  const validationMessages = {
    required: t('globals.validations.required'),
  };

  /**
   * Initialize form with Redux data if available, otherwise use default
   */
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      mixes: storeMixes && storeMixes.length > 0 ? storeMixes : [{ tranches: [{}] }],
    },
    validate: {
      mixes: (mixes) => {
        const mixErrors = mixes.map((mix) => {
          const trancheErrors = mix.tranches.map((tranche) => {
            const errors = {};
            errors.amount = validateTrancheField(
              tranche.amount,
              validationMessages.required,
            );
            errors.tranche = validateTrancheField(
              tranche.tranche,
              validationMessages.required,
            );
            errors.goal = validateTrancheField(
              tranche.goal,
              validationMessages.required,
            );
            errors.period = validateTrancheField(
              tranche.period,
              validationMessages.required,
            );

            // Remove null values to keep only actual errors
            const filteredErrors = Object.fromEntries(
              Object.entries(errors).filter(([, value]) => value !== null),
            );
            return Object.keys(filteredErrors).length ? filteredErrors : null;
          });
          return { tranches: trancheErrors };
        });

        const hasErrors = mixErrors.some((mix) => {
          const trancheHasErrors = mix.tranches.some((tranche) => tranche !== null);
          return trancheHasErrors;
        });
        return hasErrors ? mixErrors : null;
      },
    },
  });

  /**
   * Save customized mixes to the backend silently
   */
  const saveCustomizedMixesData = async (mixes) => {
    try {
      const payload = {
        mixes,
      };

      const { data, isError } = await getMutationResult(payload, apiSaveCustomizedMixes);

      if (isError) {
      // eslint-disable-next-line no-console
        console.error('Failed to save customized mixes:', data);
      }
    } catch (error) {
    // eslint-disable-next-line no-console
      console.error('Error saving customized mixes:', error);
    }
  };

  useEffect(() => {
    const fetchTranches = async () => {
      try {
        const result = await getTranchesData().unwrap();
        setTranchesData(result.tranches);
      } catch (err) {
        // Handle error silently or show notification
      }
    };
    fetchTranches();
  }, [getTranchesData]);

  const handleAddMix = () => {
    // Calculate the new index based on current length BEFORE insertion
    const newIndex = form.values.mixes.length;

    // Add the new mix
    form.insertListItem('mixes', { tranches: [{}] });

    // Switch to the new tab
    setActiveTab(`mix-${newIndex}`);
  };

  const handleRemoveMix = (e, index) => {
    e.stopPropagation();
    const newMixes = [...form.values.mixes];
    newMixes.splice(index, 1);
    form.setFieldValue('mixes', newMixes);
    setActiveTab(`mix-${newMixes.length - 1}`);
  };

  const handleAddTranche = (mixIndex) => {
    const currentTranches = [...form.values.mixes[mixIndex].tranches];
    currentTranches.push({});
    const newMixes = [...form.values.mixes];
    newMixes[mixIndex] = {
      ...newMixes[mixIndex],
      tranches: currentTranches,
    };
    form.setFieldValue('mixes', newMixes);
  };

  const handleRemoveTranche = (mixIndex, trancheIndex) => {
    const currentTranches = form.values.mixes[mixIndex].tranches;

    if (currentTranches.length > 1) {
      const newTranches = [...currentTranches];
      newTranches.splice(trancheIndex, 1);

      const newMixes = [...form.values.mixes];
      newMixes[mixIndex] = {
        ...newMixes[mixIndex],
        tranches: newTranches,
      };

      form.setFieldValue('mixes', newMixes);
    }
  };

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate the form first
      const validationResult = form.validate();
      // Check if form has errors
      if (validationResult.hasErrors) {
        // Find the first mix with errors
        const firstErrorMixIndex = validationResult.errors.mixes?.findIndex(
          (mix) => mix && mix.tranches && mix.tranches.some((tranche) => tranche !== null),
        ) ?? -1;

        if (firstErrorMixIndex >= 0) {
          setActiveTab(`mix-${firstErrorMixIndex}`);
        }
        return; // Don't continue if there are errors
      }

      // If validation passes, save and navigate
      saveCustomizedMixesData(form.values.mixes, apiSaveCustomizedMixes);
      dispatch(setMixes(form.values.mixes));
      navigate(routes.customizedMixSummary.path);
    } catch (error) {
      // Silently fail - don't notify user
    } finally {
      setLoading(false);
    }
  };

  const trancheOptions = tranchesData
    .filter((tranche) => tranche.displayInOptions)
    .map((tranche) => ({
      value: tranche.hebrew,
      label: tranche.hebrew,
    }));

  const periodOptions = Array.from({ length: 360 / 12 }, (_, i) => {
    const months = (i + 1) * 12;
    return { value: String(months), label: `${months} חודשים` };
  });

  if (tranchesData.length === 0) return <Loader />;

  const handleReturn = () => {
    navigate(-1);
  };

  return (
    <Page
      title={t('pages.customizedMix.title')}
      hideNews={true}
      hideHelp={true}
    >
      <Container size='md'>
        <ProcessHeader
          headerText={t('pages.customizedMix.headerText')}
          headerColor='#F6EBFF'
          headerTextColor='#9F1FF4'
          subtitle={t('pages.customizedMix.subtitle')}
          icon={IconSparkles}
          showReturnButton={true}
          onReturn={handleReturn}
        >
          <Flex justify='space-between' align='center' mb='md'>
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              classNames={{
                list: classes.tabList,
                tab: classes.tab,
              }}
              variant='unstyled'
            >
              <Tabs.List>
                {form.values.mixes.map((mix, index) => (
                  <Tabs.Tab key={index} value={`mix-${index}`}>
                    <Group gap={8}>
                      <Text>{t('pages.customizedMix.tab.label', { index: index + 1 })}</Text>
                      {form.values.mixes.length > 1 && (
                        <Button
                          size='xs'
                          bg={'transparent'}
                          c={'#EF4444'}
                          onClick={(e) => handleRemoveMix(e, index)}
                        >
                          <IconTrash size={16} />
                        </Button>
                      )}
                    </Group>
                  </Tabs.Tab>
                ))}

                {
                  form.values.mixes.length < MAX_MIXES
                  && <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddMix}
                    c='gray'
                    bg={'transparent'}>
                    {t('pages.customizedMix.addMix.title')}
                  </Button>
                }
              </Tabs.List>
            </Tabs>

            {form.values.mixes.length >= 2 && <Button
              rightSection={<IconArrowLeft size={24} />}
              onClick={handleContinue}
              loading={loading}
            > {t('pages.customizedMix.continueButton.title')}</Button>}
          </Flex>

          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            classNames={{
              list: classes.tabList,
              tab: classes.tab,
            }}
            variant='unstyled'
          >
            {form.values.mixes.map((mix, mixIndex) => (
              <Tabs.Panel key={mixIndex} value={`mix-${mixIndex}`} pt='md'>
                <Flex gap={'xl'} direction='column'>
                  <Flex direction='column' gap={'sm'}>
                    <Text fw={'bold'} >{t('pages.customizedMix.tab.content.title')}</Text>

                    <Stack gap='md'>
                      {mix.tranches.map((tranche, trancheIndex) => (
                        <CustomizedMixTrancheDetails
                          form={form}
                          mixIndex={mixIndex}
                          trancheIndex={trancheIndex}
                          tranchesData={tranchesData}
                          key={trancheIndex}
                          handleDelete={() => handleRemoveTranche(mixIndex, trancheIndex)}
                          trancheOptions={trancheOptions}
                          loanGoalOptions={loanGoalOptions}
                          periodOptions={periodOptions}
                        />
                      ))}

                      <Button
                        leftSection={<IconCircleDashedPlus size={16} />}
                        onClick={() => handleAddTranche(mixIndex)}
                        bg={'transparent'}
                        c={'#94A3B8'}
                        className={classes.addTrancheButton}
                      > {t('pages.customizedMix.addTranche.title')} </Button>

                    </Stack>
                  </Flex>

                  <Flex
                    gap='sm'
                    direction='column'
                  >
                    <Text fw='bold'>{`סיכום תמהיל ${mixIndex + 1}`}</Text>
                    <Grid justify='center'>
                      {(() => {
                        const totals = calculateTotalsForMix(mix);

                        const summeryBoxes = [
                          { label: t('pages.customizedMix.summaryBoxes.totalAmount'), value: formatNumberWithCommas(totals.totalLoanAmount), icon: <CustomizedMixTotalAmountIcon /> },
                          { label: t('pages.customizedMix.summaryBoxes.totalMonthlyPayment'), value: formatNumberWithCommas(totals.totalMonthlyPayment), icon: <CustomizedMixTotalReturnIcon /> },
                          { label: t('pages.customizedMix.summaryBoxes.maxMonthlyPayment'), value: formatNumberWithCommas(totals.maxMonthlyPayment), icon: <CustomizedMixMaxReturnIcon /> },
                          { label: t('pages.customizedMix.summaryBoxes.totalInterestPayment'), value: formatNumberWithCommas(totals.totalInterestPayment), icon: <CustomizedMixTotalInterestIcon /> },
                        ];

                        return summeryBoxes.map((box, index) => {
                          const { label, value, icon } = box;
                          return (<Grid.Col key={index} span={{ base: 12, sm: 3 }} p='xs'>
                            <CustomizedMixTotalBox label={label} value={value} icon={icon} />
                          </Grid.Col>
                          );
                        });
                      })()}
                    </Grid>
                  </Flex>

                </Flex>

              </Tabs.Panel>
            ))}
          </Tabs>

        </ProcessHeader>
      </Container>
    </Page>
  );
};

export default CustomizedMix;
